import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { Flame, Sparkles, Check, X, ArrowLeft, Layers, Trophy, Target, RotateCcw } from 'lucide-react';
import { useSafeThemeClasses } from '@/shared/theme';
import { useUserState } from '@/features/user/operations/UserStateProvider';
import { useFlashcardStudy } from '@/features/flashcards/useFlashcardStudy';
import type { Flashcard } from '@/features/flashcards/types';

type ThemeClasses = ReturnType<typeof useSafeThemeClasses>;
type View = 'setup' | 'study' | 'summary';

const CORRECT_PHRASES = [
  'Nailed it! 🎯', 'Crushing it! 💪', 'GMAT-ready! 🚀', 'Smooth. 😎',
  'Locked in. 🔒', "That'll stick. 🧠", 'Yes! Exactly right.', 'Clean recall! ✨',
];

const WRONG_PHRASES = [
  "No worries — it'll come back around.", 'Logged. See you again soon.',
  "That one's tricky — round two coming up.", 'Marked for a quick rematch.',
  'Good to know now, not on test day.',
];

const STREAK_MILESTONES = new Set([3, 5, 10, 15, 20, 25, 30]);

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

interface StatTileProps {
  theme: ThemeClasses;
  label: string;
  value: string | number;
  icon: React.ReactNode;
}

function StatTile({ theme, label, value, icon }: StatTileProps) {
  return (
    <div className={`p-5 rounded-2xl ${theme.card} flex items-center gap-4`}>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${theme.buttonPrimary}`}>
        {icon}
      </div>
      <div>
        <p className={`text-2xl font-bold ${theme.textHeading}`}>{value}</p>
        <p className={`text-xs ${theme.textMuted}`}>{label}</p>
      </div>
    </div>
  );
}

export default function AdminFlashcardsPage() {
  const theme = useSafeThemeClasses();
  const { user } = useUserState();
  const study = useFlashcardStudy(user?.id || user?.username);

  const [view, setView] = useState<View>('setup');
  const [confirmingReset, setConfirmingReset] = useState(false);

  const {
    topics, topicStats, selectedTopicIds, selectedCardCount, overallStats,
    toggleTopic, selectAllTopics, clearTopics,
    currentCard, isFlipped, flipCard, grade,
    startSession, endSession, stats, lastFeedback, resetAllProgress,
  } = study;

  const handleStart = () => {
    if (selectedCardCount === 0) return;
    startSession();
    setView('study');
  };

  const handleFinish = () => {
    endSession();
    setView('summary');
  };

  const handleGrade = (correct: boolean) => {
    grade(correct);
  };

  // Keyboard shortcuts during study: space/enter = flip, 1/← = wrong, 2/→ = correct
  useEffect(() => {
    if (view !== 'study' || !currentCard) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' || e.key === 'Enter') {
        e.preventDefault();
        if (!isFlipped) flipCard();
        return;
      }
      if (!isFlipped) return;
      if (e.key === '1' || e.key === 'ArrowLeft') handleGrade(false);
      if (e.key === '2' || e.key === 'ArrowRight') handleGrade(true);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, currentCard, isFlipped]);

  // Toast feedback whenever a grade lands.
  useEffect(() => {
    if (!lastFeedback) return;
    if (lastFeedback === 'correct') {
      if (STREAK_MILESTONES.has(stats.currentStreak)) {
        toast.success(`🔥 ${stats.currentStreak} in a row!`, { duration: 2200 });
      } else {
        toast.success(pickRandom(CORRECT_PHRASES), { duration: 1400 });
      }
    } else {
      toast(pickRandom(WRONG_PHRASES), { icon: '↩️', duration: 1600 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats.seen]);

  useEffect(() => {
    if (stats.masteredThisSession.length === 0) return;
    toast.success('🏆 Card mastered!', { duration: 2000, id: 'mastery' });
  }, [stats.masteredThisSession.length]);

  const accuracy = stats.seen > 0 ? Math.round((stats.correct / stats.seen) * 100) : 0;

  const topicById = useMemo(() => {
    const map = new Map<string, (typeof topics)[number]>();
    for (const t of topics) map.set(t.id, t);
    return map;
  }, [topics]);

  return (
    <div className={`min-h-screen ${theme.background}`}>
      <div className="relative max-w-5xl mx-auto px-6 pt-28 pb-24">
        <AnimatePresence mode="wait">
          {view === 'setup' && (
            <SetupView
              key="setup"
              theme={theme}
              topicStats={topicStats}
              selectedTopicIds={selectedTopicIds}
              selectedCardCount={selectedCardCount}
              overallStats={overallStats}
              toggleTopic={toggleTopic}
              selectAllTopics={selectAllTopics}
              clearTopics={clearTopics}
              onStart={handleStart}
              confirmingReset={confirmingReset}
              setConfirmingReset={setConfirmingReset}
              onResetProgress={() => {
                resetAllProgress();
                setConfirmingReset(false);
                toast.success('Progress reset. Fresh start!');
              }}
            />
          )}

          {view === 'study' && currentCard && (
            <StudyView
              key="study"
              theme={theme}
              card={currentCard}
              topicName={topicById.get(currentCard.topicId)?.name ?? ''}
              topicIcon={topicById.get(currentCard.topicId)?.icon ?? '📘'}
              topicColor={topicById.get(currentCard.topicId)?.color ?? 'from-blue-500 to-indigo-500'}
              isFlipped={isFlipped}
              onFlip={flipCard}
              onGrade={handleGrade}
              stats={stats}
              onFinish={handleFinish}
              onBack={() => { endSession(); setView('setup'); }}
            />
          )}

          {view === 'summary' && (
            <SummaryView
              key="summary"
              theme={theme}
              stats={stats}
              accuracy={accuracy}
              onStudyAgain={() => setView('setup')}
              onSameTopicsAgain={handleStart}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// ── Setup ─────────────────────────────────────────────────────────────

interface SetupViewProps {
  theme: ThemeClasses;
  topicStats: ReturnType<typeof useFlashcardStudy>['topicStats'];
  selectedTopicIds: Set<string>;
  selectedCardCount: number;
  overallStats: ReturnType<typeof useFlashcardStudy>['overallStats'];
  toggleTopic: (id: string) => void;
  selectAllTopics: () => void;
  clearTopics: () => void;
  onStart: () => void;
  confirmingReset: boolean;
  setConfirmingReset: (v: boolean) => void;
  onResetProgress: () => void;
}

function SetupView({
  theme, topicStats, selectedTopicIds, selectedCardCount, overallStats,
  toggleTopic, selectAllTopics, clearTopics, onStart,
  confirmingReset, setConfirmingReset, onResetProgress,
}: SetupViewProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4 }}
    >
      <div className="text-center mb-12">
        <h1 className={`text-4xl md:text-6xl font-bold mb-4 ${theme.textHeading}`}>
          GMAT Quant Flashcards
        </h1>
        <p className={`text-lg md:text-xl max-w-2xl mx-auto ${theme.textSecondary}`}>
          Pick your topics. The deck adapts to you — misses come back around soon, mastered cards fade into the background.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
        <StatTile theme={theme} label="Total formulas" value={overallStats.total} icon={<Layers className="w-5 h-5" />} />
        <StatTile theme={theme} label="Due for review" value={overallStats.due} icon={<Target className="w-5 h-5" />} />
        <StatTile theme={theme} label="Mastered" value={overallStats.mastered} icon={<Trophy className="w-5 h-5" />} />
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className={`text-lg font-semibold ${theme.textHeading}`}>Topics</h2>
        <div className="flex gap-2">
          <button onClick={selectAllTopics} className={`text-sm px-3 py-1.5 rounded-lg ${theme.buttonGhost} ${theme.buttonTextGhost}`}>
            Select all
          </button>
          <button onClick={clearTopics} className={`text-sm px-3 py-1.5 rounded-lg ${theme.buttonGhost} ${theme.buttonTextGhost}`}>
            Clear
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        {topicStats.map(({ topic, total, due, mastered }) => {
          const selected = selectedTopicIds.has(topic.id);
          return (
            <button
              key={topic.id}
              onClick={() => toggleTopic(topic.id)}
              className={`text-left p-5 rounded-2xl transition-all duration-200 ${theme.card} ${
                selected ? 'ring-2 ring-primary scale-[1.02] shadow-lg' : 'opacity-70 hover:opacity-100'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <span className={`text-2xl bg-gradient-to-br ${topic.color} bg-clip-text`}>{topic.icon}</span>
                {selected && <Check className="w-5 h-5 text-primary" />}
              </div>
              <h3 className={`font-semibold mb-1 ${theme.textPrimary}`}>{topic.name}</h3>
              <p className={`text-xs mb-3 ${theme.textMuted}`}>{topic.description}</p>
              <div className={`flex gap-3 text-xs ${theme.textSecondary}`}>
                <span>{total} cards</span>
                <span>·</span>
                <span>{due} due</span>
                <span>·</span>
                <span>{mastered} mastered</span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
        <button
          onClick={onStart}
          disabled={selectedCardCount === 0}
          className={`px-8 py-4 rounded-2xl font-semibold tracking-wide transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed ${theme.buttonPrimary} ${theme.buttonTextPrimary} hover:shadow-xl`}
        >
          Start Session — {selectedCardCount} card{selectedCardCount === 1 ? '' : 's'} in play
        </button>

        {!confirmingReset ? (
          <button
            onClick={() => setConfirmingReset(true)}
            className={`text-xs px-4 py-2 rounded-lg flex items-center gap-1.5 ${theme.buttonGhost} ${theme.buttonTextGhost}`}
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reset all progress
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <span className={`text-xs ${theme.textMuted}`}>Wipe all saved progress?</span>
            <button onClick={onResetProgress} className="text-xs px-3 py-1.5 rounded-lg bg-error text-white">
              Yes, reset
            </button>
            <button onClick={() => setConfirmingReset(false)} className={`text-xs px-3 py-1.5 rounded-lg ${theme.buttonGhost} ${theme.buttonTextGhost}`}>
              Cancel
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ── Study ─────────────────────────────────────────────────────────────

interface StudyViewProps {
  theme: ThemeClasses;
  card: Flashcard;
  topicName: string;
  topicIcon: string;
  topicColor: string;
  isFlipped: boolean;
  onFlip: () => void;
  onGrade: (correct: boolean) => void;
  stats: ReturnType<typeof useFlashcardStudy>['stats'];
  onFinish: () => void;
  onBack: () => void;
}

function StudyView({
  theme, card, topicName, topicIcon, topicColor, isFlipped, onFlip, onGrade, stats, onFinish, onBack,
}: StudyViewProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4 }}
    >
      <div className="flex items-center justify-between mb-8">
        <button onClick={onBack} className={`flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg ${theme.buttonGhost} ${theme.buttonTextGhost}`}>
          <ArrowLeft className="w-4 h-4" /> Topics
        </button>

        <div className="flex items-center gap-4">
          {stats.currentStreak > 1 && (
            <span className={`flex items-center gap-1 text-sm font-semibold ${theme.textAccent}`}>
              <Flame className="w-4 h-4" /> {stats.currentStreak}
            </span>
          )}
          <span className={`text-sm ${theme.textMuted}`}>
            {stats.correct}✓ &nbsp; {stats.wrong}✗
          </span>
        </div>

        <button onClick={onFinish} className={`text-sm px-3 py-2 rounded-lg ${theme.buttonSecondary} ${theme.buttonTextSecondary}`}>
          Finish
        </button>
      </div>

      <div className="flex justify-center">
        <div style={{ perspective: 1600 }} className="w-full max-w-xl">
          <motion.div
            key={card.id}
            onClick={() => !isFlipped && onFlip()}
            initial={{ rotateY: 0 }}
            animate={{ rotateY: isFlipped ? 180 : 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            style={{ transformStyle: 'preserve-3d', position: 'relative' }}
            className={`min-h-[22rem] rounded-3xl ${!isFlipped ? 'cursor-pointer' : ''}`}
          >
            {/* Front */}
            <div
              style={{ backfaceVisibility: 'hidden', position: 'absolute', inset: 0 }}
              className={`rounded-3xl p-8 md:p-10 flex flex-col ${theme.card} shadow-xl`}
            >
              <div className={`inline-flex items-center gap-2 self-start px-3 py-1 rounded-full text-xs font-medium mb-6 bg-gradient-to-r ${topicColor} text-white`}>
                <span>{topicIcon}</span> {topicName}
              </div>
              <div className="flex-1 flex items-center justify-center text-center px-2">
                <p className={`text-2xl md:text-3xl font-semibold whitespace-pre-line ${theme.textHeading}`}>
                  {card.front}
                </p>
              </div>
              <p className={`text-center text-xs mt-6 ${theme.textMuted}`}>Tap the card or press space to reveal</p>
            </div>

            {/* Back */}
            <div
              style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)', position: 'absolute', inset: 0 }}
              className={`rounded-3xl p-8 md:p-10 flex flex-col ${theme.card} shadow-xl`}
            >
              <div className={`inline-flex items-center gap-2 self-start px-3 py-1 rounded-full text-xs font-medium mb-6 bg-gradient-to-r ${topicColor} text-white`}>
                <span>{topicIcon}</span> {topicName}
              </div>
              <div className="flex-1 flex flex-col items-center justify-center text-center gap-4 px-2">
                <p className={`text-xl md:text-2xl font-bold whitespace-pre-line ${theme.textHeading}`}>
                  {card.back}
                </p>
                {card.example && (
                  <p className={`text-sm whitespace-pre-line ${theme.textSecondary}`}>
                    e.g. {card.example}
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="flex justify-center mt-8">
        {!isFlipped ? (
          <button
            onClick={onFlip}
            className={`px-8 py-3.5 rounded-2xl font-semibold ${theme.buttonPrimary} ${theme.buttonTextPrimary} hover:shadow-xl transition-all`}
          >
            Show Answer
          </button>
        ) : (
          <div className="flex gap-4">
            <button
              onClick={() => onGrade(false)}
              className="px-6 py-3.5 rounded-2xl font-semibold bg-error/15 text-error hover:bg-error/25 transition-all flex items-center gap-2"
            >
              <X className="w-5 h-5" /> Still learning
            </button>
            <button
              onClick={() => onGrade(true)}
              className="px-6 py-3.5 rounded-2xl font-semibold bg-success/15 text-success hover:bg-success/25 transition-all flex items-center gap-2"
            >
              <Check className="w-5 h-5" /> Got it
            </button>
          </div>
        )}
      </div>
      <p className={`text-center text-xs mt-4 ${theme.textMuted}`}>
        Shortcuts: space to flip · 1 = still learning · 2 = got it
      </p>
    </motion.div>
  );
}

// ── Summary ───────────────────────────────────────────────────────────

interface SummaryViewProps {
  theme: ThemeClasses;
  stats: ReturnType<typeof useFlashcardStudy>['stats'];
  accuracy: number;
  onStudyAgain: () => void;
  onSameTopicsAgain: () => void;
}

function SummaryView({ theme, stats, accuracy, onStudyAgain, onSameTopicsAgain }: SummaryViewProps) {
  const headline =
    accuracy >= 90 ? "You're on fire 🔥" :
    accuracy >= 70 ? 'Solid session 💪' :
    stats.seen === 0 ? 'Session ended' : 'Good rep — keep at it 📈';

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.4 }}
      className="text-center"
    >
      <Sparkles className={`w-10 h-10 mx-auto mb-4 ${theme.textAccent}`} />
      <h1 className={`text-4xl font-bold mb-2 ${theme.textHeading}`}>{headline}</h1>
      <p className={`text-lg mb-10 ${theme.textSecondary}`}>
        {stats.seen} card{stats.seen === 1 ? '' : 's'} reviewed this session
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl mx-auto mb-12">
        <StatTile theme={theme} label="Accuracy" value={`${accuracy}%`} icon={<Target className="w-5 h-5" />} />
        <StatTile theme={theme} label="Correct" value={stats.correct} icon={<Check className="w-5 h-5" />} />
        <StatTile theme={theme} label="Best streak" value={stats.bestStreak} icon={<Flame className="w-5 h-5" />} />
        <StatTile theme={theme} label="Mastered" value={stats.masteredThisSession.length} icon={<Trophy className="w-5 h-5" />} />
      </div>

      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <button
          onClick={onSameTopicsAgain}
          className={`px-8 py-4 rounded-2xl font-semibold ${theme.buttonPrimary} ${theme.buttonTextPrimary} hover:shadow-xl transition-all`}
        >
          Go again — same topics
        </button>
        <button
          onClick={onStudyAgain}
          className={`px-8 py-4 rounded-2xl font-semibold ${theme.card} ${theme.textPrimary} hover:shadow-lg transition-all`}
        >
          Change topics
        </button>
      </div>
    </motion.div>
  );
}
